import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { 
  Video, Upload, Wand2, Eye, Download, Trash2, 
  ArrowLeft, Play, Pause, Settings, Palette,
  Image as ImageIcon, Loader2, AlertCircle, Crown, 
  RefreshCw, Type, Music, Zap, Share2, Copy, MessageCircle, Send
} from 'lucide-react';

const AIVideoAdCreator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [images, setImages] = useState([]);
  const [adName, setAdName] = useState('');
  const [adDescription, setAdDescription] = useState('');
  const [style, setStyle] = useState('professional');
  const [duration, setDuration] = useState(15);
  const [musicStyle, setMusicStyle] = useState('upbeat');
  const [textOverlay, setTextOverlay] = useState('');
  const [ctaText, setCtaText] = useState('Contact Now');
  const [brandColor, setBrandColor] = useState('#D4AF37');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAd, setGeneratedAd] = useState(null);
  const [myAds, setMyAds] = useState([]);
  const [limits, setLimits] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [shareModal, setShareModal] = useState({ open: false, ad: null });

  // Get referral link from user
  const getReferralCode = () => user?.referral_code || user?.username || '';
  
  const getShareUrl = (adId) => {
    const baseUrl = window.location.origin;
    const referralCode = getReferralCode();
    return `${baseUrl}/video-ad/${adId}${referralCode ? `?ref=${referralCode}` : ''}`;
  };

  const handleShareAd = (ad, platform) => {
    const shareUrl = getShareUrl(ad.id);
    const shareText = `Check out this amazing video ad: "${ad.name}" - Created with ANANT Web Portal AI Tools!`;
    
    switch(platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied with your referral code!');
        break;
      default:
        break;
    }
    setShareModal({ open: false, ad: null });
  };

  useEffect(() => {
    if (user) {
      fetchLimits();
      fetchMyAds();
    }
  }, [user]);

  useEffect(() => {
    let interval;
    if (previewPlaying && images.length > 0) {
      const frameTime = (duration * 1000) / images.length;
      interval = setInterval(() => {
        setCurrentFrame(prev => {
          if (prev >= images.length - 1) {
            setPreviewPlaying(false);
            return 0;
          }
          return prev + 1;
        });
      }, frameTime);
    }
    return () => clearInterval(interval);
  }, [previewPlaying, images.length, duration]);

  const fetchLimits = async () => {
    try {
      const response = await api.get('/ai-tools/video-ad/limits');
      setLimits(response.data);
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  };

  const fetchMyAds = async () => {
    try {
      const response = await api.get('/ai-tools/video-ad/my-ads');
      setMyAds(response.data.ads || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };

  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    const maxImages = limits?.limits?.max_images_per_video || 5;
    
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, [images.length, limits]);

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const handleGenerate = async () => {
    if (!user) {
      toast.error('Please login to create video ads');
      navigate('/login');
      return;
    }

    if (images.length < 1) {
      toast.error('Please upload at least 1 image');
      return;
    }

    if (!adName.trim()) {
      toast.error('Please enter an ad name');
      return;
    }

    if (!window.confirm('AI Video Ad par 20 AT deduct hoga. Continue?')) return;

    setIsGenerating(true);
    try {
      const response = await api.post('/ai-tools/video-ad/create', {
        name: adName,
        description: adDescription,
        images: images.map(img => img.data),
        style,
        duration,
        music_style: musicStyle,
        text_overlay: textOverlay,
        cta_text: ctaText,
        brand_color: brandColor
      });

      setGeneratedAd(response.data);
      toast.success('Video ad created successfully!');
      fetchLimits();
      fetchMyAds();
      setActiveTab('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create video ad');
    } finally {
      setIsGenerating(false);
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    
    try {
      await api.delete(`/ai-tools/video-ad/${adId}`);
      toast.success('Ad deleted');
      fetchMyAds();
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const styles = [
    { value: 'professional', label: 'Professional', desc: 'Clean corporate look', color: '#3B82F6' },
    { value: 'vibrant', label: 'Vibrant', desc: 'Colorful and energetic', color: '#F59E0B' },
    { value: 'elegant', label: 'Elegant', desc: 'Luxurious and refined', color: '#8B5CF6' },
    { value: 'dramatic', label: 'Dramatic', desc: 'Bold and cinematic', color: '#EF4444' }
  ];

  const musicOptions = [
    { value: 'upbeat', label: 'Upbeat', icon: '🎵' },
    { value: 'calm', label: 'Calm', icon: '🎶' },
    { value: 'dramatic', label: 'Dramatic', icon: '🎼' },
    { value: 'none', label: 'No Music', icon: '🔇' }
  ];

  const durations = [15, 30, 60];

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-md text-center">
          <Video className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-4">AI Video Ad Creator</h2>
          <p className="text-muted-foreground mb-6">
            Transform your photos into stunning video advertisements with AI
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary w-full"
            data-testid="login-btn"
          >
            Login to Get Started
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ai-video-ad-creator">
      {/* Hero */}
      <div className="bg-gradient-to-br from-purple-500/20 via-background to-pink-500/10 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button 
            onClick={() => navigate('/tools')}
            className="flex items-center gap-2 text-muted-foreground hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </button>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">AI Video Ad Creator</h1>
                  <p className="text-sm text-muted-foreground">Photos to Video Ads with AI</p>
                </div>
              </div>
            </div>
            
            {limits && (
              <div className="glass-card px-4 py-2 flex items-center gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-primary">{limits.remaining}</div>
                  <div className="text-xs text-muted-foreground">Ads Left</div>
                </div>
                <div className="h-8 w-px bg-border"></div>
                <div className="text-center">
                  <div className="text-lg font-bold">{limits.limits?.max_video_duration || 60}s</div>
                  <div className="text-xs text-muted-foreground">Max Duration</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 mt-6">
        <div className="flex gap-2 border-b border-border">
          {[
            { id: 'create', label: 'Create Ad', icon: Wand2 },
            { id: 'my-ads', label: 'My Ads', icon: Video },
            { id: 'preview', label: 'Preview', icon: Play, disabled: !generatedAd }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-white'
              } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'create' && (
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Upload Section */}
            <div className="lg:col-span-3 space-y-6">
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-primary" />
                  Upload Product Images
                </h3>
                
                <label className="block border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                  <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Drop images here or click to upload</p>
                  <p className="text-sm text-muted-foreground">
                    Upload 1-{limits?.limits?.max_images_per_video || 5} product images
                  </p>
                </label>

                {images.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">{images.length} images uploaded</span>
                      <button
                        onClick={() => setImages([])}
                        className="text-sm text-red-500 hover:text-red-400"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {images.map((img, index) => (
                        <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden">
                          <img
                            src={img.data}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                              onClick={() => removeImage(img.id)}
                              className="p-2 bg-red-500 rounded-full"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="absolute top-1 left-1 bg-black/70 px-1.5 py-0.5 rounded text-xs">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Ad Details */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Ad Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Ad Name *</label>
                    <input
                      type="text"
                      value={adName}
                      onChange={(e) => setAdName(e.target.value)}
                      placeholder="e.g., Summer Sale Promo"
                      className="input-field w-full"
                      data-testid="ad-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={adDescription}
                      onChange={(e) => setAdDescription(e.target.value)}
                      placeholder="Describe your ad..."
                      className="input-field w-full h-20 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <Type className="w-4 h-4" />
                        Text Overlay
                      </label>
                      <input
                        type="text"
                        value={textOverlay}
                        onChange={(e) => setTextOverlay(e.target.value)}
                        placeholder="e.g., 50% OFF"
                        className="input-field w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Call to Action
                      </label>
                      <input
                        type="text"
                        value={ctaText}
                        onChange={(e) => setCtaText(e.target.value)}
                        placeholder="e.g., Shop Now"
                        className="input-field w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Settings & Generate */}
            <div className="lg:col-span-2 space-y-6">
              {/* Style Selection */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4">Ad Style</h3>
                <div className="grid grid-cols-2 gap-3">
                  {styles.map(s => (
                    <button
                      key={s.value}
                      onClick={() => setStyle(s.value)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        style === s.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-white/30'
                      }`}
                    >
                      <div 
                        className="w-8 h-8 rounded-full mb-2"
                        style={{ backgroundColor: s.color }}
                      ></div>
                      <div className="font-medium text-sm">{s.label}</div>
                      <div className="text-xs text-muted-foreground">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4">Duration</h3>
                <div className="flex gap-2">
                  {durations.map(d => (
                    <button
                      key={d}
                      onClick={() => setDuration(d)}
                      disabled={d > (limits?.limits?.max_video_duration || 60)}
                      className={`flex-1 py-3 rounded-lg border text-center transition-colors ${
                        duration === d 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-white/30'
                      } ${d > (limits?.limits?.max_video_duration || 60) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="text-xl font-bold">{d}</div>
                      <div className="text-xs text-muted-foreground">seconds</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Music */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Music className="w-5 h-5" />
                  Background Music
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {musicOptions.map(m => (
                    <button
                      key={m.value}
                      onClick={() => setMusicStyle(m.value)}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        musicStyle === m.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-white/30'
                      }`}
                    >
                      <div className="text-2xl mb-1">{m.icon}</div>
                      <div className="text-sm font-medium">{m.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand Color */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Brand Color
                </h3>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="input-field flex-1"
                  />
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating || images.length < 1 || !adName.trim()}
                className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="generate-ad-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating Video Ad...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Generate Video Ad
                  </>
                )}
              </button>

              {limits?.remaining === 0 && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-500 mb-2">
                    <Crown className="w-5 h-5" />
                    <span className="font-semibold">Limit Reached</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    You've used all your monthly video ads. Upgrade your plan for more!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'my-ads' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">My Video Ads ({myAds.length})</h2>
              <button onClick={fetchMyAds} className="btn-secondary flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {myAds.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Video className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No video ads yet</h3>
                <p className="text-muted-foreground mb-4">Create your first AI video ad!</p>
                <button onClick={() => setActiveTab('create')} className="btn-primary">
                  Create Video Ad
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myAds.map(ad => (
                  <div key={ad.id} className="glass-card overflow-hidden group">
                    <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center relative">
                      <Video className="w-12 h-12 text-muted-foreground" />
                      <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-xs">
                        {ad.duration}s
                      </div>
                      <div className="absolute top-2 left-2 px-2 py-1 rounded text-xs" style={{ backgroundColor: ad.brand_color }}>
                        {ad.style}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-1 truncate">{ad.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {ad.status === 'completed' ? `${ad.frames_generated || 0} frames` : 'Processing...'} • {ad.views || 0} views
                      </p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => window.open(`/video-ad/${ad.id}`, '_blank')}
                          className="btn-secondary flex-1 flex items-center justify-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button 
                          onClick={() => setShareModal({ open: true, ad })}
                          className="p-2 text-green-500 hover:bg-green-500/10 rounded-lg"
                          title="Share with referral link"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button 
                          className="btn-secondary flex items-center justify-center gap-1 px-3"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => deleteAd(ad.id)}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'preview' && generatedAd && (
          <div className="max-w-4xl mx-auto">
            <div className="glass-card overflow-hidden">
              {/* Preview Area */}
              <div className="aspect-video bg-black relative">
                <canvas 
                  ref={canvasRef}
                  className="w-full h-full"
                />
                
                {/* Simple Slideshow Preview */}
                {images[currentFrame] && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={images[currentFrame].data}
                      alt={`Frame ${currentFrame + 1}`}
                      className="max-w-full max-h-full object-contain transition-opacity duration-500"
                    />
                    
                    {/* Text Overlay */}
                    {textOverlay && (
                      <div 
                        className="absolute top-8 left-0 right-0 text-center"
                        style={{ color: brandColor }}
                      >
                        <span className="text-4xl font-bold drop-shadow-lg">
                          {textOverlay}
                        </span>
                      </div>
                    )}
                    
                    {/* CTA Button */}
                    {ctaText && (
                      <div className="absolute bottom-12 left-0 right-0 text-center">
                        <span 
                          className="px-8 py-3 rounded-full text-lg font-bold text-white inline-block"
                          style={{ backgroundColor: brandColor }}
                        >
                          {ctaText}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        setPreviewPlaying(!previewPlaying);
                        if (!previewPlaying) setCurrentFrame(0);
                      }}
                      className="p-3 bg-white/20 rounded-full hover:bg-white/30"
                    >
                      {previewPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                    </button>
                    
                    {/* Progress Bar */}
                    <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all"
                        style={{ width: `${((currentFrame + 1) / images.length) * 100}%` }}
                      ></div>
                    </div>
                    
                    <span className="text-sm">
                      {Math.round((currentFrame / images.length) * duration)}s / {duration}s
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-6">
                <h2 className="text-xl font-bold mb-2">{adName}</h2>
                <p className="text-muted-foreground mb-4">{adDescription || 'No description'}</p>
                
                <div className="flex flex-wrap gap-3 mb-6">
                  <span 
                    className="px-3 py-1 rounded-full text-sm"
                    style={{ backgroundColor: brandColor + '33', color: brandColor }}
                  >
                    {style} style
                  </span>
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-500 rounded-full text-sm">
                    {duration}s duration
                  </span>
                  <span className="px-3 py-1 bg-pink-500/20 text-pink-500 rounded-full text-sm">
                    {musicStyle} music
                  </span>
                  <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm">
                    {images.length} scenes
                  </span>
                </div>

                {generatedAd.status === 'completed' && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                    <p className="text-green-500 font-medium">
                      Video ad created with {generatedAd.frames_generated} AI-enhanced frames!
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button className="btn-primary flex-1 flex items-center justify-center gap-2">
                    <Download className="w-5 h-5" />
                    Download Video
                  </button>
                  <button 
                    onClick={() => setShareModal({ open: true, ad: generatedAd })}
                    className="btn-secondary flex items-center justify-center gap-2 px-6"
                  >
                    <Share2 className="w-5 h-5" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {shareModal.open && shareModal.ad && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShareModal({ open: false, ad: null })}>
          <div className="glass-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="share-modal">
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share Video Ad
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Share "{shareModal.ad.name}" with your referral link embedded!
            </p>
            
            {/* Referral Code Badge */}
            <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg mb-4">
              <p className="text-xs text-muted-foreground mb-1">Your Referral Code</p>
              <p className="font-mono font-bold text-primary">{getReferralCode() || 'Not available'}</p>
            </div>

            {/* Share Link Preview */}
            <div className="p-3 bg-white/5 rounded-lg mb-4">
              <p className="text-xs text-muted-foreground mb-1">Share Link</p>
              <p className="text-sm font-mono break-all">{getShareUrl(shareModal.ad.id)}</p>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => handleShareAd(shareModal.ad, 'whatsapp')}
                className="flex items-center justify-center gap-2 p-3 bg-green-500/20 text-green-500 rounded-xl hover:bg-green-500/30 transition-colors"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </button>
              <button
                onClick={() => handleShareAd(shareModal.ad, 'telegram')}
                className="flex items-center justify-center gap-2 p-3 bg-blue-500/20 text-blue-500 rounded-xl hover:bg-blue-500/30 transition-colors"
              >
                <Send className="w-5 h-5" />
                Telegram
              </button>
              <button
                onClick={() => handleShareAd(shareModal.ad, 'facebook')}
                className="flex items-center justify-center gap-2 p-3 bg-blue-600/20 text-blue-400 rounded-xl hover:bg-blue-600/30 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Facebook
              </button>
              <button
                onClick={() => handleShareAd(shareModal.ad, 'twitter')}
                className="flex items-center justify-center gap-2 p-3 bg-sky-500/20 text-sky-500 rounded-xl hover:bg-sky-500/30 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Twitter/X
              </button>
            </div>

            <button
              onClick={() => handleShareAd(shareModal.ad, 'copy')}
              className="w-full flex items-center justify-center gap-2 p-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors"
              data-testid="copy-link-btn"
            >
              <Copy className="w-5 h-5" />
              Copy Link with Referral
            </button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              When someone visits via your link and joins, you earn commissions!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIVideoAdCreator;
